import { useEffect, useMemo, useState } from "react";
import { FiAlertCircle, FiX } from "react-icons/fi";
import { invoiceApi } from "../../services/invoiceApi";

const money = (value) => Number(Number(value || 0).toFixed(2));

const emptyItem = () => ({
  costPriceId: "",
  description: "",
  quantity: 1,
  unitPrice: 0,
  taxRate: "",
});

const paymentModes = [
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "cash", label: "Cash" },
  { value: "card", label: "Card" },
  { value: "cheque", label: "Cheque" },
  { value: "online", label: "Online" },
  { value: "other", label: "Other" },
];


const currencyOptions = ["GBP", "USD", "EUR", "PKR", "AED", "SAR"];

const getFreightLabel = (item) => {
  return (
    item?.routeName ||
    item?.productName ||
    item?.description ||
    item?.carrier ||
    item?.forwarder ||
    `Freight Rate ${item?.id || ""}`
  );
};

const getFreightAmount = (item) => {
  return Number(
    item?.totalCost ??
      item?.total ??
      item?.oceanFreight ??
      item?.cost ??
      item?.unitPrice ??
      item?.price ??
      0
  );
};

export default function InvoiceFormModal({
  open,
  onClose,
  onSaved,
  invoice,
  vendors = [],
  costPrices = [],
  defaultCurrency = "GBP",
  defaultTaxEnabled = false,
  defaultTaxRate = 0,
  defaultDiscountEnabled = false,
  defaultDiscountRate = 0,
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [vendorId, setVendorId] = useState("");
  const [supplierName, setSupplierName] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [paymentMode, setPaymentMode] = useState("bank_transfer");
  const [statusMode, setStatusMode] = useState("auto");
  const [currency, setCurrency] = useState(defaultCurrency || "GBP");
  const [taxRate, setTaxRate] = useState(Number(defaultTaxRate || 0));
  const [discountRate, setDiscountRate] = useState(Number(defaultDiscountRate || 0));
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([emptyItem()]);

  useEffect(() => {
    if (!open) return;

    setError("");

    if (invoice) {
      setVendorId(invoice.vendorId || "");
      setSupplierName(invoice.supplierName || "");
      setInvoiceDate(invoice.invoiceDate || "");
      setDueDate(invoice.dueDate || "");
      setPaymentMode(invoice.paymentMode || "bank_transfer");
      setStatusMode(
        invoice.status === "draft" && Number(invoice.paidAmount || 0) === 0
          ? "draft"
          : "auto"
      );
      setCurrency(invoice.currency || defaultCurrency || "GBP");
      setTaxRate(Number(defaultTaxRate || 0));
      setDiscountRate(Number(defaultDiscountRate || 0));
      setNotes(invoice.notes || "");
      setItems(
        invoice.items?.length
          ? invoice.items.map((line) => ({
              costPriceId: line.costPriceId || "",
              description: line.description || "",
              quantity: Number(line.quantity || 1),
              unitPrice: Number(line.unitPrice || 0),
              taxRate:
                line.taxRate !== undefined && line.taxRate !== null
                  ? Number(line.taxRate)
                  : "",
            }))
          : [emptyItem()]
      );
    } else {
      setVendorId("");
      setSupplierName("");
      setInvoiceDate(new Date().toISOString().slice(0, 10));
      setDueDate("");
      setPaymentMode("bank_transfer");
      setStatusMode("auto");
      setCurrency(defaultCurrency || "GBP");
      setTaxRate(Number(defaultTaxEnabled ? defaultTaxRate : 0));
      setDiscountRate(Number(defaultDiscountEnabled ? defaultDiscountRate : 0));
      setNotes("");
      setItems([emptyItem()]);
    }
  }, [open, invoice, defaultCurrency, defaultTaxEnabled, defaultTaxRate, defaultDiscountEnabled, defaultDiscountRate]);

  const currentPaidAmount = money(invoice?.paidAmount || 0);

  const totals = useMemo(() => {
    let subtotal = 0;
    let totalTax = 0;

    items.forEach((item) => {
      const quantity = Number(item.quantity || 0);
      const unitPrice = Number(item.unitPrice || 0);
      const lineBase = quantity * unitPrice;
      const rate =
        item.taxRate === "" || item.taxRate === null || item.taxRate === undefined
          ? Number(defaultTaxEnabled ? defaultTaxRate : taxRate || 0)
          : Number(item.taxRate || 0);

      subtotal += lineBase;
      totalTax += lineBase * (rate / 100);
    });

    subtotal = money(subtotal);
    totalTax = money(totalTax);

    const discount = money(
      defaultDiscountEnabled
        ? subtotal * (Number(defaultDiscountRate || 0) / 100)
        : subtotal * (Number(discountRate || 0) / 100)
    );

    const totalAmount = money(subtotal + totalTax - discount);
    const balanceAmount = money(totalAmount - currentPaidAmount);

    return {
      subtotal,
      tax: totalTax,
      discount,
      totalAmount,
      balanceAmount,
    };
  }, [
    items,
    taxRate,
    discountRate,
    currentPaidAmount,
    defaultTaxEnabled,
    defaultTaxRate,
    defaultDiscountEnabled,
    defaultDiscountRate,
  ]);

  const setItemField = (index, field, value) => {
    setItems((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;

        const updated = { ...item, [field]: value };

        if (field === "costPriceId") {
          const selected = costPrices.find((cp) => String(cp.id) === String(value));
          if (selected) {
            updated.description = getFreightLabel(selected);
            updated.unitPrice = getFreightAmount(selected);
            if (!updated.taxRate && defaultTaxEnabled) {
              updated.taxRate = Number(defaultTaxRate || 0);
            }
          }
        }

        return updated;
      })
    );
  };

  const addItem = () => setItems((prev) => [...prev, emptyItem()]);
  const removeItem = (index) => {
    setItems((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      if (!invoiceDate) throw new Error("Invoice date is required");
      if (!vendorId && !supplierName.trim()) {
        throw new Error("Please select a forwarder or enter supplier name");
      }

      const cleanedItems = items
        .map((item) => {
          const quantity = Number(item.quantity || 0);
          const unitPrice = Number(item.unitPrice || 0);
          const lineBase = money(quantity * unitPrice);
          const resolvedTaxRate =
            item.taxRate === "" || item.taxRate === null || item.taxRate === undefined
              ? Number(defaultTaxEnabled ? defaultTaxRate : taxRate || 0)
              : Number(item.taxRate || 0);

          return {
            costPriceId: item.costPriceId ? Number(item.costPriceId) : null,
            description: (item.description || "").trim(),
            quantity,
            unitPrice,
            taxRate: resolvedTaxRate,
            taxAmount: money(lineBase * (resolvedTaxRate / 100)),
          };
        })
        .filter((item) => item.description && item.quantity > 0);

      if (cleanedItems.length === 0) {
        throw new Error("Add at least one valid invoice item");
      }

      if (currentPaidAmount > totals.totalAmount) {
        throw new Error(
          "Invoice total cannot be reduced below the amount already paid. Adjust or refund the payment first."
        );
      }

      const selectedVendor = vendors.find((v) => String(v.id) === String(vendorId));

      const payload = {
        vendorId: vendorId ? Number(vendorId) : null,
        supplierName: supplierName.trim() || selectedVendor?.name || null,
        invoiceDate,
        dueDate: dueDate || null,
        paymentMode,
        status: statusMode === "draft" ? "draft" : undefined,
        currency,
        taxMode: "percent",
        taxRate: Number(defaultTaxEnabled ? defaultTaxRate : taxRate || 0),
        taxAmount: totals.tax,
        discountAmount: totals.discount,
        notes: notes.trim() || null,
        items: cleanedItems,
      };

      const res = invoice
        ? await invoiceApi.update(invoice.id, payload)
        : await invoiceApi.create(payload);

      onSaved?.(res.data?.message || "Invoice saved successfully");
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to save invoice");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const inputClass =
    "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 disabled:bg-slate-100 disabled:text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100";
  const labelClass =
    "mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300";
  const tableInputClass =
    "w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-blue-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100";

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/40 px-4 py-6 backdrop-blur-[2px]">
      <div className="max-h-[92vh] w-full max-w-6xl overflow-y-auto rounded-3xl border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-start justify-between border-b border-slate-100 px-6 py-5 dark:border-slate-800">
          <div>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
              {invoice ? "Edit Freight Invoice" : "New Freight Invoice"}
            </h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Create or update forwarder invoices using freight rates and logistics charges.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="grid h-10 w-10 place-items-center rounded-full border border-slate-200 text-slate-500 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            <FiX />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-6 p-6">
          {error ? (
            <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300">
              <FiAlertCircle className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          ) : null}

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div>
              <label className={labelClass}>Forwarder</label>
              <select
                value={vendorId}
                onChange={(e) => {
                  setVendorId(e.target.value);
                  const selected = vendors.find((v) => String(v.id) === String(e.target.value));
                  if (selected && !supplierName) setSupplierName(selected.name || "");
                }}
                className={inputClass}
                disabled={saving}
              >
                <option value="">Select forwarder</option>
                {vendors.map((vendor) => (
                  <option key={vendor.id} value={vendor.id}>
                    {vendor.name || `Forwarder ${vendor.id}`}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>Supplier / Forwarder Name</label>
              <input
                value={supplierName}
                onChange={(e) => setSupplierName(e.target.value)}
                className={inputClass}
                placeholder="Forwarder or supplier name"
                disabled={saving}
              />
            </div>

            <div>
              <label className={labelClass}>Invoice Date</label>
              <input
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
                className={inputClass}
                disabled={saving}
                required
              />
            </div>

            <div>
              <label className={labelClass}>Due Date</label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className={inputClass}
                disabled={saving}
              />
            </div>

            <div>
              <label className={labelClass}>Payment Mode</label>
              <select
                value={paymentMode}
                onChange={(e) => setPaymentMode(e.target.value)}
                className={inputClass}
                disabled={saving}
              >
                {paymentModes.map((mode) => (
                  <option key={mode.value} value={mode.value}>
                    {mode.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>Status</label>
              <select
                value={statusMode}
                onChange={(e) => setStatusMode(e.target.value)}
                className={inputClass}
                disabled={saving || currentPaidAmount > 0}
              >
                <option value="auto">
                  Automatic — based on due date and payments
                </option>
                <option value="draft" disabled={currentPaidAmount > 0}>
                  Draft
                </option>
              </select>
              <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
                Pending, partial, paid, and overdue are calculated automatically.
              </p>
            </div>

            <div>
              <label className={labelClass}>Currency</label>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className={inputClass}
                disabled={saving}
              >
                {currencyOptions.map((cur) => (
                  <option key={cur} value={cur}>
                    {cur}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>Paid Amount</label>
              <input
                type="text"
                value={`${currency} ${currentPaidAmount.toFixed(2)}`}
                className={inputClass}
                readOnly
                disabled
              />
              <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
                Managed only through the Payments module.
              </p>
            </div>

            <div>
              <label className={labelClass}>Tax %</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={defaultTaxEnabled ? defaultTaxRate : taxRate}
                onChange={(e) => setTaxRate(e.target.value)}
                className={inputClass}
                disabled={saving || defaultTaxEnabled}
              />
            </div>

            <div>
              <label className={labelClass}>Discount %</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={defaultDiscountEnabled ? defaultDiscountRate : discountRate}
                onChange={(e) => setDiscountRate(e.target.value)}
                className={inputClass}
                disabled={saving || defaultDiscountEnabled}
              />
            </div>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-slate-200 dark:border-slate-800">
            <table className="min-w-full">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:bg-slate-950 dark:text-slate-400">
                <tr>
                  <th className="px-4 py-3">Freight Rate</th>
                  <th className="px-4 py-3">Description</th>
                  <th className="px-4 py-3">Qty</th>
                  <th className="px-4 py-3">Unit Price</th>
                  <th className="px-4 py-3">Tax %</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {items.map((item, index) => {
                  const lineAmount = money(Number(item.quantity || 0) * Number(item.unitPrice || 0));

                  return (
                    <tr key={index} className="bg-white text-slate-700 dark:bg-slate-900 dark:text-slate-200">
                      <td className="px-4 py-3">
                        <select
                          value={item.costPriceId}
                          onChange={(e) => setItemField(index, "costPriceId", e.target.value)}
                          className={tableInputClass}
                          disabled={saving}
                        >
                          <option value="">Manual item</option>
                          {costPrices.map((cp) => (
                            <option key={cp.id} value={cp.id}>
                              {getFreightLabel(cp)} — {cp.currency || currency} {getFreightAmount(cp).toFixed(2)}
                            </option>
                          ))}
                        </select>
                      </td>

                      <td className="px-4 py-3">
                        <input
                          value={item.description}
                          onChange={(e) => setItemField(index, "description", e.target.value)}
                          className={tableInputClass}
                          placeholder="Freight, customs, port charge..."
                          disabled={saving}
                        />
                      </td>

                      <td className="px-4 py-3">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.quantity}
                          onChange={(e) => setItemField(index, "quantity", e.target.value)}
                          className={tableInputClass}
                          disabled={saving}
                        />
                      </td>

                      <td className="px-4 py-3">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.unitPrice}
                          onChange={(e) => setItemField(index, "unitPrice", e.target.value)}
                          className={tableInputClass}
                          disabled={saving}
                        />
                      </td>

                      <td className="px-4 py-3">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.taxRate}
                          onChange={(e) => setItemField(index, "taxRate", e.target.value)}
                          className={tableInputClass}
                          placeholder={String(defaultTaxEnabled ? defaultTaxRate : taxRate || 0)}
                          disabled={saving}
                        />
                      </td>

                      <td className="px-4 py-3 font-semibold text-slate-900 dark:text-slate-100">
                        {currency} {lineAmount.toFixed(2)}
                      </td>

                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          disabled={saving || items.length === 1}
                          className="rounded-xl border border-red-200 px-3 py-2 text-sm text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-900/50 dark:text-red-300 dark:hover:bg-red-950/40"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <button
            type="button"
            onClick={addItem}
            disabled={saving}
            className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:opacity-60"
          >
            + Add Item
          </button>

          <div className="grid gap-4 md:grid-cols-4">
            <div className="rounded-2xl bg-slate-50 p-4 dark:bg-slate-950">
              <p className="text-sm text-slate-500 dark:text-slate-400">Subtotal</p>
              <p className="mt-2 text-lg font-semibold">{currency} {totals.subtotal.toFixed(2)}</p>
            </div>
            <div className="rounded-2xl bg-slate-50 p-4 dark:bg-slate-950">
              <p className="text-sm text-slate-500 dark:text-slate-400">Tax</p>
              <p className="mt-2 text-lg font-semibold">{currency} {totals.tax.toFixed(2)}</p>
            </div>
            <div className="rounded-2xl bg-slate-50 p-4 dark:bg-slate-950">
              <p className="text-sm text-slate-500 dark:text-slate-400">Discount</p>
              <p className="mt-2 text-lg font-semibold">{currency} {totals.discount.toFixed(2)}</p>
            </div>
            <div className="rounded-2xl bg-blue-50 p-4 dark:bg-blue-950/50">
              <p className="text-sm text-slate-500 dark:text-slate-400">Balance</p>
              <p className="mt-2 text-lg font-semibold">{currency} {totals.balanceAmount.toFixed(2)}</p>
            </div>
          </div>

          <div>
            <label className={labelClass}>Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows="3"
              className={inputClass}
              placeholder="Optional invoice notes"
              disabled={saving}
            />
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-slate-100 pt-4 dark:border-slate-800">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-2xl bg-blue-600 px-6 py-3 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {saving ? "Saving..." : invoice ? "Update Invoice" : "Create Invoice"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
